import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../header/header';
import { constantStyles } from '../constants/constantStyles';
import { useMediaQuery } from 'react-responsive';
import { useIsAdmin } from '../auth/useAdmin';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../utils/loadingState';
import MediaDisplay from '../components/MediaDisplay';

const endpoint = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_SERVER_URL
  : process.env.REACT_APP_LOCAL_SERVER_URL;

const Home = () => {
  const isMobile = useMediaQuery({ maxWidth: constantStyles.mobileWidth });
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  // State for dynamic images
  const [images, setImages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback images (current hardcoded images)
  const fallbackImages = {
    main_banner: {
      desktop_url: '/images/home_page/fallback_desktop.webp',
      mobile_url: '/images/home_page/mobile/fallback_mobile.webp',
      alt_text: 'featured fall styles image',
      destination_url: '/featured'
    },
    promotional: [
      {
        desktop_url: '/images/home_page/sweaters.jpg',
        mobile_url: '/images/home_page/mobile/sweaters.jpg',
        alt_text: "women's sweaters image",
        destination_url: '/women?categories=3'
      },
      {
        desktop_url: '/images/home_page/mini_skirts.jpg',
        mobile_url: '/images/home_page/mobile/mini_skirts.jpg',
        alt_text: 'mini skirts image',
        destination_url: '/women?categories=16'
      },
      {
        desktop_url: '/images/home_page/men_hoodies.jpg',
        mobile_url: '/images/home_page/mobile/men_hoodies.jpg',
        alt_text: "men's hoodies image",
        destination_url: '/men?categories=10'
      }
    ]
  };

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await fetch(`${endpoint}api/home-page-images`);

        if (!response.ok) {
          throw new Error('Failed to fetch home page images');
        }

        const data = await response.json();
        setImages(data);
      } catch (err) {
        console.error('Error fetching home page images:', err);
        setError(err.message);
        // Use fallback images on error
        setImages(fallbackImages);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Use fallback while loading or if no images
  const displayImages = images || fallbackImages;

  const mainBanner = displayImages.main_banner;
  const [leftImage, middleImage, rightImage] = displayImages.promotional || [];

  // Show loading state with bunny animation
  if (loading) {
    return (
      <div>
        <Header />
        <div style={styles.loadingScreenWrapper}>
        <LoadingScreen />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>The Sidewalk - Discover Emerging Fashion Brands</title>
        <meta name="description" content="Discover the latest fashion from emerging brands like Tiger Mist, Levi's, Cotton On, and 33mm. Shop women's and men's clothing, sweaters, mini skirts, hoodies, and more." />

        {/* Canonical URL - tells Google this is the authoritative version */}
        <link rel="canonical" href="https://thesidewalk.co/" />

        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="The Sidewalk - Discover Emerging Fashion Brands" />
        <meta property="og:description" content="Shop the latest fashion from emerging brands. Women's and men's clothing curated for style." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thesidewalk.co/" />
        <meta property="og:site_name" content="The Sidewalk" />
        <meta property="og:image" content="https://thesidewalk.co/images/home_page/fallback_desktop.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Sidewalk - Discover Emerging Fashion Brands" />
        <meta name="twitter:description" content="Shop the latest fashion from emerging brands." />
        <meta name="twitter:image" content="https://thesidewalk.co/images/home_page/fallback_desktop.webp" />

        {/* Structured data for Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "The Sidewalk",
            "url": "https://thesidewalk.co/",
            "description": "Discover the latest fashion from emerging brands like Tiger Mist, Levi's, Cotton On, and 33mm.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://thesidewalk.co/women?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      <Header />

      {/* Admin Button - Only visible to admins */}
      {isAdmin && (
        <div style={styles.adminButtonContainer}>
          <button
            onClick={() => navigate('/admin/home-images')}
            style={styles.adminButton}
          >
            Change Images
          </button>
        </div>
      )}

      {/* Main Banner */}
      {mainBanner && (
        <div style={styles.mainContainer}>
          <div
            style={styles.mainImageContainer}
            onClick={() => window.location.href = mainBanner.destination_url}
          >
            <MediaDisplay
              media={mainBanner}
              isMobile={isMobile}
              style={styles.mainImage}
              alt={mainBanner.alt_text}
              priority={true}
            />
          </div>
        </div>
      )}

      {/* Promotional Images - Mobile Layout */}
      {isMobile && (
        <>
          {leftImage && (
            <div style={styles.mainContainer}>
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = leftImage.destination_url}
              >
                <MediaDisplay
                  media={leftImage}
                  isMobile={isMobile}
                  style={styles.image}
                  alt={leftImage.alt_text}
                />
              </div>
            </div>
          )}
          {middleImage && (
            <div style={styles.mainContainer}>
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = middleImage.destination_url}
              >
                <MediaDisplay
                  media={middleImage}
                  isMobile={isMobile}
                  style={styles.image}
                  alt={middleImage.alt_text}
                />
              </div>
            </div>
          )}
          {rightImage && (
            <div style={styles.mainContainer}>
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = rightImage.destination_url}
              >
                <MediaDisplay
                  media={rightImage}
                  isMobile={isMobile}
                  style={styles.image}
                  alt={rightImage.alt_text}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Promotional Images - Desktop Layout */}
      {!isMobile && (
        <div style={styles.secondContainer}>
          <div style={styles.secondContainerImages}>
            {leftImage && (
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = leftImage.destination_url}
              >
                <MediaDisplay
                  media={leftImage}
                  isMobile={isMobile}
                  style={{ ...styles.image, ...styles.imageLeft }}
                  alt={leftImage.alt_text}
                />
              </div>
            )}
            {middleImage && (
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = middleImage.destination_url}
              >
                <MediaDisplay
                  media={middleImage}
                  isMobile={isMobile}
                  style={styles.image}
                  alt={middleImage.alt_text}
                />
              </div>
            )}
            {rightImage && (
              <div
                style={styles.imageContainer}
                onClick={() => window.location.href = rightImage.destination_url}
              >
                <MediaDisplay
                  media={rightImage}
                  isMobile={isMobile}
                  style={{ ...styles.image, ...styles.imageRight }}
                  alt={rightImage.alt_text}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;

const styles = {
  adminButtonContainer: {
    position: 'relative',
    margin: '20px 5%',
    marginBottom: '0',
  },
  adminButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#0056b3',
    }
  },
  mainContainer: {
    display: 'flex',
    margin: '20px 5% 40px 5%',
    justifyContent: 'center',
  },
  secondContainer: {
    margin: '20px 5% 20px 5%',
    display: 'flex',
    justifyContent: 'center',
  },
  secondContainerImages: {
    maxWidth: '1600px',
    justifyContent: 'space-between',
    gap: '2%',
    display: 'flex',
  },
  mainImageContainer: {
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  mainImage: {
    maxWidth: '1600px',
    width: '100%',
  },
  imageContainer: {
    cursor: 'pointer',
  },
  imageLeft: {
    borderTopLeftRadius: '20px',
    borderBottomLeftRadius: '20px',
  },
  imageRight: {
    borderTopRightRadius: '20px',
    borderBottomRightRadius: '20px',
  },
  image: {
    width: '100%',
  },
  loadingScreenWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
};
